import React from "react";
import { Row, Table, Skeleton, Pagination, Tooltip } from "antd";
import moment from "moment";
import { useDispatch, useSelector } from "react-redux";
import {
  selectMaterialRequestHistorySearchParams,
  setMaterialRequestHistoryPagination,
} from "../../redux/slices/materialRequestHistorySlice";
import DownloadFileIcon from "../Icons/DownloadFileIcon";

import { stringSorter } from "../../utils/sortUtils";
import {
  checkMaterialHistoryValue,
  formatMaterialRequestDate,
  getMaterialHistoryItems,
  isRequestHistoryLoading,
  shouldShowRequestHistoryMessage,
  shouldShowRequestHistoryTable,
} from "../../utils/materialRequestUtils";
import { getAccumulatorInstruction } from "../../utils/accumulatorInstructions";

const splitMaterialLine = (line) => {
  const [boxText, accumulatorText] = line.split(" Acumuladores: ");

  return {
    boxText,
    accumulatorText: `Acumuladores: ${accumulatorText}`,
  };
};

const renderMaterials = (row) => {
  const items = getMaterialHistoryItems(row);

  if (!items.length) return "N/D";

  return (
    <div className="materials-requested-cell">
      {items.map((item) => {
        const { line, boxKey, accumulatorCode } = item;
        const { boxText, accumulatorText } = splitMaterialLine(line);
        const instruction = getAccumulatorInstruction(
          row?.temperatureRange,
          boxKey,
          accumulatorCode,
        );

        return (
          <div className="materials-requested-row" key={line}>
            <span className="materials-requested-box">{boxText}</span>
            <span className="materials-requested-accum">
              {accumulatorText}
              {instruction && (
                <Tooltip title="Instrucciones de preparación">
                  <a
                    className="material-instructions-download"
                    href={instruction.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    download={instruction.file}
                    aria-label="Instrucciones de preparación"
                  >
                    <DownloadFileIcon />
                  </a>
                </Tooltip>
              )}
            </span>
          </div>
        );
      })}
    </div>
  );
};

export default function RequestHistoryTable({
  requestHistory,
  fetchRequestHistory,
}) {
  const dispatch = useDispatch();
  const searchParams = useSelector(selectMaterialRequestHistorySearchParams);

  const isLoading = isRequestHistoryLoading(requestHistory);
  const data = requestHistory?.data || [];

  const columns = [
    {
      title: "Contrato",
      dataIndex: "contractNumber",
      width: 40,
      sorter: stringSorter("contractNumber"),
      sortDirections: ["descend", "ascend"],
      align: "center",
    },
    {
      title: "Detallable",
      dataIndex: "clientNumber",
      width: 40,
      sorter: stringSorter("clientNumber"),
      sortDirections: ["descend", "ascend"],
      align: "center",
    },
    {
      title: "Etiquetador",
      dataIndex: "labellerCode",
      width: 50,
      sorter: stringSorter("labellerCode"),
      sortDirections: ["descend", "ascend"],
      align: "center",
    },
    {
      title: "Fecha y hora",
      dataIndex: "requestDate",
      width: 40,
      sorter: (a, b) =>
        moment(a.requestDate).valueOf() - moment(b.requestDate).valueOf(),
      sortDirections: ["descend", "ascend"],
      align: "center",
      render: formatMaterialRequestDate,
    },
    {
      title: "Rango de temperatura",
      dataIndex: "temperatureRange",
      width: 50,
      sorter: stringSorter("temperatureRange"),
      sortDirections: ["descend", "ascend"],
      align: "center",
    },
    {
      title: "Materiales solicitados",
      dataIndex: "materials",
      width: 120,
      align: "center",
      render: (_, row) => renderMaterials(row),
    },
    {
      title: "Observaciones",
      dataIndex: "observations",
      width: 80,
      align: "center",
      render: checkMaterialHistoryValue,
    },
  ];

  const handlePageChange = (page, size) => {
    dispatch(setMaterialRequestHistoryPagination({ page, size }));

    fetchRequestHistory({
      ...searchParams,
      page,
      size,
      pagination: true,
    });
  };

  const handlePageSizeChange = (page, size) => {
    dispatch(setMaterialRequestHistoryPagination({ page: 1, size }));

    fetchRequestHistory({
      ...searchParams,
      page: 1,
      size,
      pagination: true,
    });
  };

  const paginatorConfig = {
    position: "top",
    pageSize: requestHistory?.size || 10,
    current: requestHistory?.page || 1,
    total: requestHistory?.totalResults || data.length,
    showSizeChanger: true,
    showQuickJumper: true,
    showTotal: (total) => `${total} resultados`,
    pageSizeOptions: ["10", "20", "40", "80"],
  };

  if (isLoading) {
    return (
      <div className="RequestHistoryTable">
        <Row>
          <div className="results-title">
            <h2>Resultados</h2>
          </div>
        </Row>
        <Row>
          <Skeleton active />
        </Row>
      </div>
    );
  }

  if (shouldShowRequestHistoryTable(requestHistory)) {
    return (
      <div className="RequestHistoryTable">
        <Row>
          <div className="results-title">
            <h2>Resultados</h2>
          </div>

          <div className="tracks-table-scroll">
            <Table
              columns={columns}
              dataSource={data}
              bordered
              rowKey={(row) => row.id}
              pagination={false}
              scroll={{ x: 1200 }}
              loading={requestHistory?.status === "paginating"}
            />
          </div>
        </Row>

        <Row type="flex" justify="end" className="pagination-row">
          <Pagination
            {...paginatorConfig}
            onChange={handlePageChange}
            onShowSizeChange={handlePageSizeChange}
          />
        </Row>
      </div>
    );
  }

  if (shouldShowRequestHistoryMessage(requestHistory)) {
    return (
      <div className="RequestHistoryTable">
        <div className="results-title">
          <h2>Resultados</h2>
        </div>
        <Row>
          <p>
            {requestHistory?.message ||
              "No se han encontrado resultados para la búsqueda realizada."}
          </p>
        </Row>
      </div>
    );
  }

  return null;
}
